import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfigProvider, Table, Dropdown, Select, TreeSelect, message } from 'antd';
import type { TableColumnsType, ThemeConfig, MenuProps } from 'antd';
import { fontFamily } from '../../theme/tokens';
import Pagination from '../../components/Pagination';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import {
  useGetUsersQuery,
  useDeleteUserMutation,
  useImportUsersMutation,
  useLazyGetUserImportTemplateQuery,
  userName,
  userEmail,
  userPhone,
  userDirectory,
  userDesignationList,
  userHierarchy,
  userRoleNames,
  type AppUser,
  useGetDesignationQuery,
} from '../../features/users/usersApi';
import {
  useGetNodeTreeQuery,
  type OrgNode,
} from '../../features/organizations/organizationsApi';
import { useActiveOrgId } from '../../features/organizations/useActiveOrg';
import {
  useGetStateDepartmentQuery,
  useGetUnitsQuery,
} from '../../features/platform/platformApi';
import { usePlatformState } from '../../features/platform/platformStateSlice';
import { keyId, nodeKey } from '../../components/OrgNodeTree';
import LocationCascade from '../../components/LocationCascade';
import { PARTIES, ELECTION_RESULTS } from './createUser/types';
import BulkAddUsersModal from './BulkAddUsersModal';
import {
  UploadIcon,
  DownloadIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  SearchIcon,
  CaretIcon,
  DirectoryIcon,
  ClearFilterIcon,
} from '../../assets/icons';

const TABLE_THEME: ThemeConfig = {
  token: { colorBorderSecondary: '#e2e8f0', fontFamily },
  components: {
    Table: {
      headerBg: '#eef4ff',
      headerColor: '#64748b',
      headerSplitColor: 'transparent',
      borderColor: '#f1f5f9',
      cellPaddingBlock: 18,
      cellPaddingInline: 24,
      rowHoverBg: '#f8fafc',
      colorText: '#334155',
      fontSize: 14,
    },
  },
};

/** Directory pill styling + label per directory type (Figma 1291-3231). */
const DIRECTORY_META: Record<string, { label: string; cls: string }> = {
  elected: { label: 'Political Person', cls: 'border-[#a7f3d0] bg-[#ecfdf5] text-[#047857]' },
  non_elected: {
    label: 'Important Person',
    cls: 'border-[#a7f3d0] bg-[#ecfdf5] text-[#047857]',
  },
  bureaucracy: { label: 'Government Official', cls: 'border-[#bfdbfe] bg-[#eff6ff] text-[#1d4ed8]' },
  party: { label: 'Party Person', cls: 'border-[#fde68a] bg-[#fffbeb] text-[#b45309]' },
  organization: { label: 'Client Team User', cls: 'border-[#e9d5ff] bg-[#faf5ff] text-[#7e22ce]' },
};
const directoryMeta = (d: string) =>
  DIRECTORY_META[d.toLowerCase()] ?? {
    label: d || '—',
    cls: 'border-[#e2e8f0] bg-[#f1f5f9] text-[#475569]',
  };

/** Format one number into "+91 XXXXX XXXXX" when it's a 10-digit mobile. */
function formatOne(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`;
  return raw.trim();
}
/** Format a phone value (single, or comma-joined multiple) for display. */
function formatPhone(raw: string): string {
  if (!raw) return '—';
  return raw
    .split(',')
    .map((p) => formatOne(p))
    .filter(Boolean)
    .join(', ');
}

/** A pill-style filter dropdown (icon + label + chevron) matching the design. */
function FilterDropdown({
  icon,
  label,
  value,
  options,
  onChange,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  const items: MenuProps['items'] = [
    { key: '', label: `All ${label}` },
    ...options.map((o) => ({ key: o.value, label: o.label })),
  ];
  const current = options.find((o) => o.value === value);
  return (
    <Dropdown
      menu={{ items, selectable: true, selectedKeys: [value], onClick: ({ key }) => onChange(key) }}
      trigger={['click']}
    >
      <button
        type="button"
        className="flex shrink-0 items-center gap-2 rounded-lg border border-[#e2e8f0] bg-white px-[15px] py-[9px] text-sm text-[#334155] transition-colors hover:bg-[#f8fafc]"
      >
        <span className="flex size-4 items-center justify-center text-[#64748b]">{icon}</span>
        <span className="whitespace-nowrap">{current ? current.label : label}</span>
        <CaretIcon className="size-3 text-[#64748b]" />
      </button>
    </Dropdown>
  );
}

/** Shared pill height so the antd Select/TreeSelect filters line up with the pill buttons. */
const FILTER_CONTROL_HEIGHT = 38;

/** Page title per locked directory (used by the directory-scoped sub-pages). */
const LOCKED_DIRECTORY_TITLE: Record<string, string> = {
  elected: 'Political Persons',
  bureaucracy: 'Government Officials',
  party: 'Party Persons',
  organization: 'Client Team Users',
  non_elected: 'Important Persons',
};

/** Representative location options per department level (anchored levels whose places come
 *  from LGD and aren't in the units API). Own levels use their real units. */
const DEPT_SAMPLE_LOCATIONS: Record<string, string[]> = {
  Division: ['Jaipur Division'],
  District: ['Alwar', 'Khairthal-Tijara', 'Kotputli-Behror'],
  'Sub-division': ['Alwar SD', 'Ramgarh SD', 'Rajgarh SD', 'Laxmangarh SD', 'Kathumar SD', 'Thanagazi SD', 'Tijara SD', 'Kishangarh Bas SD', 'Behror SD'],
  Tehsil: ['Alwar', 'Ramgarh', 'Rajgarh', 'Laxmangarh', 'Kathumar', 'Malakhera', 'Thanagazi', 'Reni', 'Tijara', 'Kishangarh Bas', 'Behror', 'Bansur', 'Mundawar', 'Neemrana', 'Kotkasim', 'Umrain', 'Govindgarh'],
  'Patwar Circle': ['Alwar circle 1', 'Behror circle 1', 'Neemrana circle 1', 'Reni circle 1'],
  Village: ['Karanpura', 'Harsora', 'Bahadarpur', 'Naugaon'],
};

export default function UsersPage({
  lockedDirectory,
  createPath,
  deptCode,
}: { lockedDirectory?: string; createPath?: string; deptCode?: string } = {}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [directory, setDirectory] = useState(lockedDirectory ?? '');
  const [location, setLocation] = useState('');
  const [designation, setDesignation] = useState('');
  // Department-flow (People tab): cascading Level name → Post → Location filters.
  const [levelName, setLevelName] = useState('');
  const [department, setDepartment] = useState('');
  // Political Person only — 'elected' | 'defeated' | '' (All Candidates).
  const [candidateOutcome, setCandidateOutcome] = useState('');
  // Political Person only — party name filter.
  const [partyName, setPartyName] = useState('');
  // Organization comes from the header-wide picker (not a per-page filter).
  const organization = useActiveOrgId();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [userToDelete, setUserToDelete] = useState<AppUser | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  // Org-node location filter: raw typed text + its debounced value (sent to the backend).
  const [locQuery, setLocQuery] = useState('');
  const [locSearch, setLocSearch] = useState('');

  // The Location picker mirrors the Create-User form: elected/bureaucracy use the EC
  // location cascade, party uses the org-node hierarchy, org members have no location.
  const isCascadeDir =
    directory === 'elected' || directory === 'non_elected' || directory === 'bureaucracy';
  const isOrgDir = directory === 'organization';

  // Directory-scoped sub-pages: keep `directory` pinned to the route's locked value and clear
  // the directory-specific filters when switching between them (the component instance is reused).
  useEffect(() => {
    if (lockedDirectory === undefined) return;
    setDirectory(lockedDirectory);
    setLocation('');
    setLocQuery('');
    setDesignation('');
    setLevelName('');
    setDepartment('');
    setCandidateOutcome('');
    setPartyName('');
    setPage(1);
  }, [lockedDirectory]);

  // Debounce the search box so we don't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(query.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [query]);

  // Debounce the location TreeSelect's typed text into the server-side node-tree search.
  useEffect(() => {
    const t = setTimeout(() => setLocSearch(locQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [locQuery]);

  // Switching the header organization resets the location (it belongs to the old org's tree)
  // and jumps back to the first page.
  useEffect(() => {
    setLocation('');
    setLocQuery('');
    setPage(1);
  }, [organization]);

  // `location` holds either an EC area code (cascade directories) or an org-node tree
  // key like "n:15" (party) — send the code as-is, or the node's numeric id.
  const { data, isFetching, refetch } = useGetUsersQuery({
    search,
    directory,
    location: location ? (isCascadeDir ? location : keyId(location)) : undefined,
    designation,
    department,
    candidateOutcome: candidateOutcome || undefined,
    partyName: partyName || undefined,
    organization: organization ?? undefined,
    page,
    pageSize,
  });
  const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation();
  const [importUsers, { isLoading: isImporting }] = useImportUsersMutation();
  const [downloadTemplate, { isFetching: isDownloading }] = useLazyGetUserImportTemplateQuery();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Location tree + template/import all scope to the header-selected organization.
  const treeOrg = organization ?? undefined;
  const { data: orgTree, isFetching: orgTreeLoading } = useGetNodeTreeQuery(
    { organization: treeOrg as number, ...(locSearch ? { search: locSearch } : {}) },
    { skip: treeOrg == null },
  );
  // Org-node hierarchy as antd TreeSelect data (value = "n:<id>" selection key).
  const orgTreeData = useMemo(() => {
    type TSNode = { value: string; title: string; children?: TSNode[] };
    const toNode = (n: OrgNode): TSNode => {
      const kids = (n.children ?? []).map(toNode);
      return {
        value: nodeKey(Number(n.id)),
        title:
          [n.org_name, n.level_name].filter(Boolean).join(' - ') || n.org_name || `Node ${n.id}`,
        ...(kids.length ? { children: kids } : {}),
      };
    };
    return (orgTree ?? []).map(toNode);
  }, [orgTree]);

  // Download the users template (.xlsx, one sheet per directory) for the organization.
  const handleDownloadTemplate = async () => {
    if (treeOrg == null) {
      message.warning('No organization available to download a template.');
      return;
    }
    try {
      const blob = await downloadTemplate({ organization: treeOrg }).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'users-template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      message.error('Could not download the template. Please try again.');
    }
  };

  // Bulk upload a filled template (organization + file).
  const handleFilePicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    if (treeOrg == null) {
      message.warning('No organization available for the upload.');
      return;
    }
    try {
      const res = await importUsers({ file, organization: treeOrg }).unwrap();
      const created = res?.created ?? 0;
      const failed = res?.failed ?? res?.errors?.length ?? 0;
      if (failed) {
        message.warning(`Import finished: ${created} added, ${failed} row(s) had errors.`);
      } else {
        message.success(res?.detail || `Import successful — ${created} user(s) added.`);
      }
    } catch {
      message.error('Bulk upload failed. Check the file format and try again.');
    }
  };

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Directory filter — the four real directories (matches the Create User screen's tabs).
  // `non_elected` stays in DIRECTORY_META only for rendering the table pill, not as a filter.
  const directoryOptions = Object.entries(DIRECTORY_META)
    .filter(([value]) => value !== 'non_elected')
    .map(([value, m]) => ({ value, label: m.label }));

  // Designation & Department filter options — distinct values for the selected org, scoped to
  // the chosen directory (Government Official designations differ from Political Person, etc.).
  const { data: fieldOptions } = useGetDesignationQuery(
    { organization: treeOrg, directory: directory || undefined },
    { skip: treeOrg == null },
  );
  const toOptions = (values: string[] = []) => values.map((v) => ({ value: v, label: v }));
  const designationOptions = useMemo(
    () => toOptions(fieldOptions?.designations),
    [fieldOptions],
  );
  const departmentOptions = useMemo(() => toOptions(fieldOptions?.departments), [fieldOptions]);

  /* ---- Department-flow cascading filters: Level name → Post → Location ---- */
  const { code: platformStateCode, name: platformStateName } = usePlatformState();
  const deptMode = Boolean(deptCode);
  const useElectedPosts = lockedDirectory === 'elected';
  const { data: deptDetail } = useGetStateDepartmentQuery(
    { code: platformStateCode, tcode: deptCode ?? '' },
    { skip: !deptMode || !deptCode },
  );
  const deptLevelOptions = useMemo(
    () => (deptDetail?.levels ?? []).map((l) => ({ value: l.name, label: l.name })),
    [deptDetail],
  );
  const deptPostOptions = useMemo(
    () =>
      (deptDetail?.levels ?? [])
        .filter((l) => (!levelName || l.name === levelName) && (useElectedPosts ? l.elected_post : l.official_post))
        .map((l) => {
          const post = useElectedPosts ? l.elected_post : l.official_post;
          return { value: post, label: post, level: l.name };
        }),
    [deptDetail, levelName, useElectedPosts],
  );
  const selectedPostLevel = deptPostOptions.find((p) => p.value === designation)?.level ?? levelName;
  const { data: deptUnits } = useGetUnitsQuery(
    { code: platformStateCode, template_code: deptCode ?? '', level: selectedPostLevel },
    { skip: !deptMode || !selectedPostLevel },
  );
  const deptLocationOptions = useMemo(() => {
    const unitNames = (deptUnits?.units ?? []).map((u) => u.name);
    const names = unitNames.length
      ? unitNames
      : selectedPostLevel === 'State'
        ? [platformStateName]
        : DEPT_SAMPLE_LOCATIONS[selectedPostLevel] ?? [];
    return names.map((n) => ({ value: n, label: n }));
  }, [deptUnits, selectedPostLevel, platformStateName]);

  const hasFilters = Boolean(
    query ||
      directory ||
      location ||
      designation ||
      department ||
      candidateOutcome ||
      partyName ||
      levelName,
  );
  const clearFilters = () => {
    setQuery('');
    setDirectory('');
    setLocation('');
    setLevelName('');
    setLocQuery('');
    setDesignation('');
    setDepartment('');
    setCandidateOutcome('');
    setPartyName('');
    setPage(1);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;
    try {
      await deleteUser(userToDelete.id).unwrap();
      setUserToDelete(null);
      // Pull a fresh page from the server so the deleted user drops off immediately.
      refetch();
    } catch {
      /* keep the modal open so the user can retry */
    }
  };

  const columns: TableColumnsType<AppUser> = [
    {
      title: 'User Name & Email',
      key: 'user',
      width: 232,
      render: (_, u) => (
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-semibold text-[#0f172a]">{userName(u)}</span>
          <span className="text-xs text-[#64748b]">{userEmail(u) || '—'}</span>
        </div>
      ),
    },
    {
      title: 'Contact Details',
      key: 'contact',
      width: 156,
      render: (_, u) => (
        <span className="font-mono text-xs text-[#475569]">{formatPhone(userPhone(u))}</span>
      ),
    },
    {
      title: 'Designation',
      key: 'designation',
      width: 180,
      render: (_, u) => {
        const desig = userDesignationList(u);
        if (!desig.length) {
          const role = userRoleNames(u)[0];
          return role ? (
            <span className="text-sm font-medium text-[#334155]">{role}</span>
          ) : (
            <span className="text-sm text-[#94a3b8]">—</span>
          );
        }
        return (
          <div className="flex flex-wrap gap-1">
            {desig.map((t, i) => (
              <span
                key={`d-${i}`}
                className="inline-flex rounded-md bg-[#eef4ff] px-2 py-0.5 text-xs font-medium text-brand"
              >
                {t}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      title: 'Location',
      key: 'location',
      width: 146,
      render: (_, u) => {
        const loc = u.location_name || userHierarchy(u);
        return <span className="text-sm text-[#475569]">{loc && loc !== '—' ? loc : '—'}</span>;
      },
    },
    {
      title: 'Directory',
      key: 'directory',
      width: 178,
      render: (_, u) => {
        const meta = directoryMeta(userDirectory(u));
        if (!userDirectory(u)) return <span className="text-sm text-[#94a3b8]">—</span>;
        return (
          <span
            className={[
              'inline-flex items-center rounded-full border px-[11px] py-[5px] text-xs font-medium',
              meta.cls,
            ].join(' ')}
          >
            {meta.label}
          </span>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 102,
      align: 'right',
      render: (_, u) => (
        <div className="flex items-center justify-end gap-1.5">
          <button
            type="button"
            aria-label="Edit user"
            onClick={() => navigate(`/admin/users/${u.id}/edit`)}
            className="flex size-8 items-center justify-center rounded-lg text-[#64748b] transition-colors hover:bg-brand-shade hover:text-brand"
          >
            <PencilIcon className="size-4" />
          </button>
          <button
            type="button"
            aria-label="Delete user"
            onClick={() => setUserToDelete(u)}
            className="flex size-8 items-center justify-center rounded-lg text-[#64748b] transition-colors hover:bg-[#fee2e2] hover:text-[#dc2626]"
          >
            <TrashIcon className="size-4" />
          </button>
        </div>
      ),
    },
  ];

  // Embedded in the Government page / department People tab (createPath is passed) — hide the
  // page title and the Download Template / Bulk Upload actions; keep only Create.
  const embedded = Boolean(createPath);

  return (
    <div className="flex flex-col gap-6">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        {!embedded && (
          <div className="flex flex-col gap-1">
            <h1 className="text-2xl font-semibold leading-9 text-[#00173e]">
              {lockedDirectory ? (LOCKED_DIRECTORY_TITLE[lockedDirectory] ?? 'Users Directory') : 'Users Directory'}
            </h1>
            <p className="text-sm text-[#46556f]">
              Manage administrative personnel, roles, and organizational hierarchies.
            </p>
          </div>
        )}
        {/* The All-Person overview hides the action buttons; every concrete directory
            page (incl. Important Person) keeps them. */}
        {lockedDirectory !== undefined && (
        <div className="flex flex-nowrap gap-3 sm:ml-auto [&>button]:shrink-0 [&>button]:whitespace-nowrap">
          {!embedded && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                onChange={handleFilePicked}
              />
              <button
                type="button"
                onClick={handleDownloadTemplate}
                disabled={isDownloading}
                className="flex items-center gap-2 rounded-md border border-[#cbd5e1] bg-white px-[17px] py-[9px] text-sm font-medium text-[#00173e] transition-colors hover:bg-brand-shade disabled:cursor-not-allowed disabled:opacity-60"
              >
                <DownloadIcon className="size-4" />
                {isDownloading ? 'Preparing…' : 'Download Template'}
              </button>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isImporting}
                className="flex items-center gap-2 rounded-md border border-[#cbd5e1] bg-white px-[17px] py-[9px] text-sm font-medium text-[#00173e] transition-colors hover:bg-brand-shade disabled:cursor-not-allowed disabled:opacity-60"
              >
                <UploadIcon className="h-[14px] w-[12px]" />
                {isImporting ? 'Uploading…' : 'Bulk Upload'}
              </button>
            </>
          )}
          <button
            type="button"
            // Locked directory pages live at /admin/users/<slug>; their create form is the nested
            // "new" route (e.g. /admin/users/political/new) so the sidebar link stays active.
            onClick={() => navigate(createPath ?? (lockedDirectory ? 'new' : '/admin/users/new'))}
            className="flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-medium text-white shadow-btn transition-colors hover:bg-brand-dark"
          >
            <PlusIcon className="size-3.5" />
            Create User
          </button>
        </div>
        )}
      </div>

      {/* Search + filters — a single row; the organization is chosen in the header, so the
          per-page org selector is gone. The controls flex to share the width responsively. */}
      <div className="flex flex-nowrap items-center gap-3">
        <div className="relative min-w-0 flex-1">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[#64748b]" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, email, contact..."
            className="w-full rounded-lg border border-[#e2e8f0] bg-white py-2.5 pl-9 pr-4 text-sm text-ink placeholder:text-[#46556f] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15"
          />
        </div>
        {/* Directory is chosen via the sidebar's Directory group (each is its own page), so the
            Directory picker is no longer shown on the list. */}
        {/* Political Person → Candidate Outcome (election_result), right after Directory. */}
        {directory === 'elected' && (
          <div className="min-w-0 flex-1">
            <Select
              className="w-full"
              style={{ height: FILTER_CONTROL_HEIGHT }}
              value={candidateOutcome || 'all'}
              options={[
                { value: 'all', label: 'All Candidates' },
                ...ELECTION_RESULTS.map((r) => ({ value: r, label: r })),
              ]}
              onChange={(v) => {
                setCandidateOutcome(v === 'all' ? '' : v);
                setPage(1);
              }}
            />
          </div>
        )}
        {/* Political Person → Party Name filter. */}
        {directory === 'elected' && (
          <div className="min-w-0 flex-1">
            <Select
              className="w-full"
              placeholder="Party Name"
              showSearch
              optionFilterProp="label"
              allowClear
              style={{ height: FILTER_CONTROL_HEIGHT }}
              value={partyName || undefined}
              options={PARTIES.map((p) => ({ value: p, label: p }))}
              onChange={(v) => {
                setPartyName(v ?? '');
                setPage(1);
              }}
            />
          </div>
        )}
        {/* Department-flow cascading filters: Level name → Post → Location. */}
        {deptMode && (
          <>
            <div className="min-w-0 flex-1">
              <Select
                className="w-full"
                placeholder="Level name"
                showSearch
                optionFilterProp="label"
                allowClear
                style={{ height: FILTER_CONTROL_HEIGHT }}
                value={levelName || undefined}
                options={deptLevelOptions}
                onChange={(v) => {
                  setLevelName(v ?? '');
                  setDesignation('');
                  setLocation('');
                  setPage(1);
                }}
              />
            </div>
            <div className="min-w-0 flex-1">
              <Select
                className="w-full"
                placeholder="Post"
                showSearch
                optionFilterProp="label"
                allowClear
                style={{ height: FILTER_CONTROL_HEIGHT }}
                value={designation || undefined}
                options={deptPostOptions}
                onChange={(v) => {
                  setDesignation(v ?? '');
                  setLocation('');
                  setPage(1);
                }}
              />
            </div>
            <div className="min-w-0 flex-1">
              <Select
                className="w-full"
                placeholder={designation ? 'Location' : 'Select a post first'}
                showSearch
                optionFilterProp="label"
                allowClear
                disabled={!designation}
                style={{ height: FILTER_CONTROL_HEIGHT }}
                value={location || undefined}
                options={deptLocationOptions}
                onChange={(v) => {
                  setLocation(v ?? '');
                  setPage(1);
                }}
              />
            </div>
          </>
        )}
        {/* Location filter — matches the Create-User form per directory.
            Org members have no location, so it's hidden for that directory. */}
        {!deptMode && (isOrgDir ? null : isCascadeDir ? (
          <div className="min-w-0 flex-1">
            <LocationCascade
              organization={treeOrg}
              value={location}
              placeholder="Location"
              onChange={(sel) => {
                setLocation(sel ? String(sel.value) : '');
                setPage(1);
              }}
            />
          </div>
        ) : (
          <div className="min-w-0 flex-1">
            <TreeSelect
              className="w-full"
              placeholder="Location"
              showSearch
              // Server-side search: don't filter locally; the node-tree query returns matches.
              filterTreeNode={false}
              searchValue={locQuery}
              onSearch={setLocQuery}
              onOpenChange={(open) => {
                if (!open) setLocQuery('');
              }}
              loading={orgTreeLoading}
              notFoundContent={orgTreeLoading ? 'Searching…' : 'No matches'}
              treeDefaultExpandAll
              allowClear
              // Taller, scrollable, wider popup so the whole org tree (all SKs) is reachable.
              virtual={false}
              listHeight={400}
              popupMatchSelectWidth={360}
              style={{ height: FILTER_CONTROL_HEIGHT }}
              value={location || undefined}
              treeData={orgTreeData}
              onChange={(v) => {
                setLocation(v ?? '');
                setLocQuery('');
                setPage(1);
              }}
            />
          </div>
        ))}
        {/* Party Person has neither Designation nor Department filters. */}
        {!deptMode && directory !== 'party' && (
          <div className="min-w-0 flex-1">
            <Select
              className="w-full"
              placeholder="Designation"
              showSearch
              optionFilterProp="label"
              allowClear
              style={{ height: FILTER_CONTROL_HEIGHT }}
              value={designation || undefined}
              options={designationOptions}
              onChange={(v) => {
                setDesignation(v ?? '');
                setPage(1);
              }}
            />
          </div>
        )}
        {/* Department — hidden for Political Person (Candidate Outcome instead), Party Person,
            and in the department flow (the department is already fixed). */}
        {!deptMode && directory !== 'elected' && directory !== 'party' && (
          <div className="min-w-0 flex-1">
            <Select
              className="w-full"
              placeholder="Department"
              showSearch
              optionFilterProp="label"
              allowClear
              style={{ height: FILTER_CONTROL_HEIGHT }}
              value={department || undefined}
              options={departmentOptions}
              onChange={(v) => {
                setDepartment(v ?? '');
                setPage(1);
              }}
            />
          </div>
        )}
        {hasFilters && (
          <button
            type="button"
            onClick={clearFilters}
            className="flex shrink-0 items-center gap-1.5 whitespace-nowrap text-sm font-medium text-[#64748b] transition-colors hover:text-brand"
          >
            <ClearFilterIcon className="size-4" />
            Clear Filters
          </button>
        )}
      </div>

      {/* Data table */}
      <div className="overflow-hidden rounded-xl border border-[#e2e8f0] bg-white shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]">
        <ConfigProvider theme={TABLE_THEME}>
          <Table<AppUser>
            className="chunaav-table"
            columns={columns}
            dataSource={items}
            rowKey="id"
            pagination={false}
            loading={isFetching}
            scroll={{ x: 'max-content' }}
          />
        </ConfigProvider>

        {/* Footer / pagination */}
        <Pagination
          rangeStart={total === 0 ? 0 : (page - 1) * pageSize + 1}
          rangeEnd={Math.min(page * pageSize, total)}
          total={total}
          noun="entries"
          page={page}
          totalPages={totalPages}
          onChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(v) => {
            setPageSize(v);
            setPage(1);
          }}
        />
      </div>

      <ConfirmDeleteModal
        open={userToDelete !== null}
        title="Delete User"
        message={
          <>
            Are you sure you want to delete{' '}
            <span className="font-semibold text-ink">
              “{userToDelete ? userName(userToDelete) : ''}”
            </span>
            ? This action is permanent and cannot be undone.
          </>
        }
        loading={isDeleting}
        onClose={() => setUserToDelete(null)}
        onConfirm={confirmDelete}
      />

      <BulkAddUsersModal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        organization={treeOrg}
      />
    </div>
  );
}
